import type { Slide } from "./types";

// ─────────────────────────────────────────────────────────────────────
// Slide data
// ─────────────────────────────────────────────────────────────────────

/** Portfolio projects; order = scroll sequence. Used by carousel and scripts/bake-carousel-strip.mjs. */
export const SLIDES: readonly Slide[] = [
    {
        image: "facetovoice.com.png",
        slug: "face-to-voice",
        title: "Face to Voice",
        subtitle: "3D Characters & Voice AI",
        link: "https://facetovoice.com",
        role: "Design, Development",
        completed: "2026",
        repo: "https://github.com/OliverA100/face-to-voice",
        tech: ["TypeScript", "Next.js", "Three.js", "GLSL", "GSAP", "Python", "Claude API", "ElevenLabs API", "LiveKit", "Node.js"],
        textColor: "#000000",
        bgColor: "rgb(245, 243, 241)",
    },
    {
        image: "hdholdings.ca.png",
        slug: "hdholdings",
        title: "HD Holdings",
        subtitle: "Real Estate & Development",
        link: "https://hdholdings.ca/",
        role: "Design, Development",
        completed: "2026",
        tech: ["TypeScript", "React", "Vite", "Tailwind CSS", "GSAP", "Node.js"],
        textColor: "#FF2600",
    },
    {
        image: "the-pokemon-wall.png",
        slug: "the-pokemon-wall",
        title: "The Pokémon Wall",
        subtitle: "Pokédex & Craft",
        link: "https://the-pokemon-wall.vercel.app",
        role: "Design, Development",
        completed: "2026",
        repo: "https://github.com/OliverA100/the-pokemon-wall",
        tech: ["TypeScript", "React", "Three.js", "GLSL", "Vite", "Tailwind CSS", "Node.js"],
        // The brand color (#efece5, warm cream) is too light to carry text,
        // so unlike other slides the roles are inverted: the cream is the
        // panel BACKGROUND and the text color is the site's own warm
        // near-black.
        textColor: "#16150F",
        bgColor: "#efece5",
    },
    {
        image: "halo.png",
        slug: "halo",
        title: "Halo",
        subtitle: "Product Concept & Ambient Storytelling",
        link: "https://halo-nine-rho.vercel.app/",
        role: "Design, Development",
        completed: "2026",
        tech: ["TypeScript", "Next.js", "Three.js", "GSAP", "Node.js"],
        textColor: "#585AFD",
        bgColor: "color-mix(in srgb, #585AFD 4%, #FFFFFF)",
    },
    {
        image: "soquelec.com.png",
        slug: "soquelec",
        title: "Soquelec",
        subtitle: "Electron Microscopy & Imaging",
        link: "https://www.soquelec.com",
        role: "Design, Development",
        completed: "2023",
        tech: ["JavaScript", "Gatsby.js", "Sanity.io", "GraphQL", "styled-components", "Material UI", "Node.js"],
        textColor: "#100E49",
    },
    {
        image: "soquelectelecom.com.png",
        slug: "soquelec-telecom",
        title: "Soquelec Telecom",
        subtitle: "RF, Microwave & Fibre Solutions",
        link: "https://soquelectelecom.com/",
        role: "Design, Development",
        completed: "2025",
        tech: ["TypeScript", "Next.js", "Three.js", "Tailwind CSS", "Node.js"],
        textColor: "#567AFF",
    },
    {
        image: "oliverhassan.dev.png",
        slug: "portfolio-site",
        title: "Portfolio",
        subtitle: "Selected Work & Experiments",
        link: "https://oliverhassan.dev",
        role: "Design, Development",
        completed: "2025",
        tech: ["TypeScript", "React", "Vite", "Three.js", "Tailwind CSS", "GSAP", "Framer Motion", "Node.js"],
        textColor: "#1c1c1c",
        badge: "current",
    },
    {
        image: "crypto-revolution.netlify.app.png",
        slug: "crypto-revolution",
        title: "Crypto Revolution",
        subtitle: "Visuals & Volatility",
        link: "https://crypto-revolution.netlify.app",
        role: "Design, Development",
        completed: "2024",
        tech: ["TypeScript", "React", "Vite", "Three.js", "Tailwind CSS", "Node.js"],
        textColor: "#000000",
        bgColor: "color-mix(in srgb, #000000 12%, #FFFFFF)",
        badge: "development",
    },
];

// ─────────────────────────────────────────────────────────────────────
// Tech badge colors
// ─────────────────────────────────────────────────────────────────────

/** Tech names → brand colors for AnimatedTitle badge pills. */
export const TECH_COLORS: Record<string, string> = {
    JavaScript: "rgb(223, 188, 19, 1)",
    TypeScript: "rgb(49, 120, 198, 1)",
    React: "rgb(113, 192, 230, 1)",
    "Next.js": "rgb(0, 0, 0, 1)",
    "Gatsby.js": "rgb(102, 51, 153, 1)",
    Vite: "rgb(100, 108, 255, 1)",
    GraphQL: "rgb(225, 0, 152, 1)",
    "Three.js": "rgb(255, 255, 255, 1)",
    "Sanity.io": "rgb(240, 62, 47, 1)",
    HTML: "rgb(227, 79, 38, 1)",
    CSS: "rgb(38, 77, 228, 1)",
    "Tailwind CSS": "rgb(6, 182, 212, 1)",
    "styled-components": "rgb(255, 137, 137, 1)",
    "Material UI": "rgb(0, 127, 255, 1)",
    GSAP: "rgb(95,202,49)",
    GLSL: "rgb(85, 134, 164, 1)",
    "Framer Motion": "rgb(234,202,0)",
    "Node.js": "rgb(68, 136, 62, 1)",
    Python: "rgb(55, 118, 171, 1)",
    "Claude API": "rgb(217, 119, 87, 1)",
    "ElevenLabs API": "rgb(0, 0, 0, 1)",
    LiveKit: "rgb(31, 213, 249, 1)",
};

// ─────────────────────────────────────────────────────────────────────
// Layout
// ─────────────────────────────────────────────────────────────────────

/** Extra space above the first slide (in slide units). getCenterIndex uses it so progress 0 gives a negative center index. */
export const HEADROOM_SLIDES = 0.12;

/** Pixels subtracted from scroll target when centering a slide. ScrollRail compensates so the frame aligns with the active indicator. */
export const SCROLL_CENTER_OFFSET_PX = 80;
